import { BluetoothLowEnergyAdapterProvider } from './adapter/BluetoothLowEnergyAdapterProvider';
import { BluetoothLowEnergyNamespaces } from './adapter/AbstractBluetoothLowEnergyAdapter';
import { BluetoothLowEnergyEventChannel, IBluetoothLowEnergyEvent, IBluetoothLowEnergyEventSubscription } from './BluetoothLowEnergyEventChannel';

const NAMESPACE = BluetoothLowEnergyNamespaces.PERIPHERAL;

/**
 * The peripheral role: advertising, hosting a service, answering the reads and writes a connected
 * central performs, and notifying subscribers. This is the role a tool wants when the device is the
 * thing being connected TO.
 *
 * Order matters more here than in the central role. Open the event channel, register the connection
 * listener, host the service, then advertise — in that sequence, because a central can connect the
 * instant advertising starts and a service registered after that is not there to be discovered.
 *
 * Every read request, and every write request whose `responseNeeded` is true, must be answered with
 * {@link respondToRequest} quoting the event's `requestId`. The connected central is blocked until it
 * is. Values are Base64 in both directions, and nothing fragments — see
 * `BluetoothLowEnergyCentralService` for both conventions in full.
 *
 * One asymmetry to know before relying on it: {@link close} withdraws this side of each connection and
 * clears the hosted service, but a central that initiated the connection keeps its link and receives
 * no disconnect. Its next write fails with an invalid-handle status instead. `close` is not a way to
 * make a peer notice it has been dropped.
 *
 * Not every device can do this at all — check `BluetoothLowEnergyAdapterService.isPeripheralModeSupported`
 * first. Only available inside the CandyCane application; on the Velocity browser every method rejects.
 *
 * ## Errors
 *
 * A rejection carries `{ code, message }`. Shared codes: `INVALID_OPTIONS`, `UNKNOWN_METHOD`,
 * `INITIALIZATION_ERROR`, `PLUGIN_NOT_READY`.
 *
 * Codes specific to this role: `ADVERTISING_START_FAILED`, `ADVERTISING_STOP_FAILED`,
 * `SERVICE_REGISTRATION_FAILED`, `SERVICE_REMOVAL_FAILED`, `RESPOND_FAILED`, `NOTIFY_FAILED`,
 * `CLOSE_FAILED`, `LISTENER_REGISTRATION_FAILED`, `LISTENER_UNREGISTRATION_FAILED`.
 */
export class BluetoothLowEnergyPeripheralService {
    /**
     * Starts advertising.
     *
     * @param config optional, every member optional:
     *   ```
     *   {
     *     data?: {
     *       includeDeviceName?: boolean,
     *       serviceUUIDs?: string[],
     *       serviceData?: { [serviceUUID: string]: string },
     *       manufacturerData?: { [companyId: string]: string }
     *     },
     *     config?: {
     *       mode?: 'LOW_POWER' | 'BALANCED' | 'LOW_LATENCY',
     *       txPowerLevel?: 'ULTRA_LOW' | 'LOW' | 'MEDIUM' | 'HIGH',
     *       connectable?: boolean,
     *       timeoutMillis?: number
     *     },
     *     scanResponse?: { ...same shape as data }
     *   }
     *   ```
     *   The `serviceData` and `manufacturerData` values are Base64, keyed by service identifier and
     *   company identifier respectively. An absent `config` lets the model's defaults apply, and an
     *   absent `data` advertises nothing but the address — legal, and how a connectable beacon with no
     *   payload is expressed. `scanResponse` carries payload that does not fit the 31-byte
     *   advertisement, at the cost of a scanning peer having to ask for it.
     * @return {Promise} resolves `{ advertising: true }`.
     */
    public static startAdvertising(config?: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'startAdvertising', config);
    }

    /**
     * Stops advertising. Existing connections are unaffected.
     *
     * @return {Promise} resolves `{ advertising: false }`.
     */
    public static stopAdvertising(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'stopAdvertising');
    }

    /**
     * Hosts a service and starts reporting the reads and writes centrals perform against it as
     * `readRequest` and `writeRequest` events.
     *
     * @param config `{ service: { uuid: string, characteristics: [{ uuid, properties, permissions }] } }`.
     *   `characteristics` must be non-empty — a service with nothing to read, write or notify has
     *   nothing to offer a central.
     * @return {Promise} resolves `{ serviceUUID: string }`.
     */
    public static addService(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'addService', config);
    }

    /**
     * Stops hosting a service.
     *
     * @param config `{ serviceUUID: string }`.
     * @return {Promise} resolves `{ serviceUUID: string }`.
     */
    public static removeService(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'removeService', config);
    }

    /**
     * Answers a pending read or write request. Required for every read request and every write request
     * whose `responseNeeded` is true; the central is blocked until it arrives.
     *
     * @param config `{ requestId: string, status: 'SUCCESS' | 'READ_NOT_PERMITTED' | 'WRITE_NOT_PERMITTED' | 'INVALID_OFFSET' | 'FAILURE', value?: string }`.
     *   `requestId` comes from the request event verbatim. `value` is the data a successful read
     *   returns, and is unused when acknowledging a write.
     * @return {Promise} resolves `{ requestId: string }`.
     */
    public static respondToRequest(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'respondToRequest', config);
    }

    /**
     * Pushes a new characteristic value to subscribers.
     *
     * @param config `{ serviceUUID, characteristicUUID, value, indicate?, deviceAddress? }`, `value` Base64.
     *   `indicate` asks for an acknowledged notification. Omitting `deviceAddress` notifies every
     *   subscriber; naming one targets it alone. A value larger than the packet size negotiated with the
     *   target fails rather than fragmenting; that size arrives as a `packetSize` event.
     * @return {Promise} resolves `{ notified: true }`.
     */
    public static notifyCharacteristicChanged(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'notifyCharacteristicChanged', config);
    }

    /**
     * Starts reporting the three connection-level streams: `centralConnection`, `subscriptionState`
     * and `packetSize`.
     *
     * Register before advertising. Packet size in particular bounds every later notification, and until
     * a central negotiates one the safe assumption is the Bluetooth default of 23 bytes.
     *
     * @return {Promise} resolves `{ registered: true }`.
     */
    public static registerEventListener(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'registerEventListener');
    }

    /**
     * Stops reporting the connection-level streams.
     *
     * @return {Promise} resolves `{ unregistered: true }`.
     */
    public static unregisterEventListener(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'unregisterEventListener');
    }

    /**
     * Releases the hosted service and stops advertising. See the class documentation for what this does
     * not do to an already-connected central.
     *
     * @return {Promise} resolves `{ closed: true }`.
     */
    public static close(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'close');
    }

    /**
     * Starts receiving this role's events and delivers each to `handler`.
     *
     * Open this before advertising or hosting a service. Events produced with no channel open are
     * dropped, and a dropped `readRequest` leaves a central waiting.
     *
     * | `event.type` | `event.payload` |
     * |---|---|
     * | `readRequest` | `{ requestId, deviceAddress, serviceUUID, characteristicUUID, offset }` |
     * | `writeRequest` | `{ requestId, deviceAddress, serviceUUID, characteristicUUID, value, offset, responseNeeded }` |
     * | `centralConnection` | `{ deviceAddress, state }` |
     * | `subscriptionState` | `{ deviceAddress, serviceUUID, characteristicUUID, subscribed }` |
     * | `packetSize` | `{ deviceAddress, packetSize }` |
     *
     * Every `readRequest`, and every `writeRequest` whose `responseNeeded` is true, must be answered
     * with {@link respondToRequest} quoting that event's `requestId`.
     *
     * @param handler invoked once per event.
     * @return {Promise} resolves a subscription whose `off()` detaches the handler. Prefer `off()` over
     *   {@link closeEventChannel} for per-component cleanup — closing is tool-level and stops the
     *   stream for every subscriber in this frame.
     */
    public static openEventChannel(handler: (event: IBluetoothLowEnergyEvent) => void): Promise<IBluetoothLowEnergyEventSubscription> {
        return BluetoothLowEnergyEventChannel.open(NAMESPACE, handler);
    }

    /**
     * Stops receiving this role's events and detaches every handler.
     *
     * @return {Promise} resolves `{ closed: boolean }`, false when no channel was open.
     */
    public static closeEventChannel(): Promise<any> {
        return BluetoothLowEnergyEventChannel.close(NAMESPACE);
    }
}
